import React from 'react';
import { surfaces } from '../../styles/colors';

interface CardProps {
  children: React.ReactNode;
  className?: string;
  id?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  /** When true, height follows content instead of filling parent (avoids h-full). */
  fitContent?: boolean;
  [key: `data-${string}`]: string | undefined;
}

const Card: React.FC<CardProps> = ({ children, className = '', id, padding = 'md', fitContent = false, ...dataProps }) => {
  const paddingClasses = {
    none: '',
    sm: 'p-3 sm:p-4',
    // Compact default; roomier padding only on large screens
    md: 'p-3 sm:p-4 xl:pt-4 xl:px-6 xl:pb-6',
    lg: 'p-4 sm:p-6 xl:p-8',
  };
  const heightClass = fitContent ? 'h-auto min-h-0' : 'h-full';

  return (
    <div 
      id={id}
      className={`bg-white ${surfaces.radius} border border-gray-200 ${surfaces.elevation} ${paddingClasses[padding]} flex flex-col ${heightClass} ${className} transition-shadow duration-200 ${surfaces.elevationHover}`}
      {...dataProps}
    >
      {children}
    </div>
  );
};

interface CardHeaderProps {
  children: React.ReactNode;
  className?: string;
}

export const CardHeader: React.FC<CardHeaderProps> = ({ children, className = '' }) => {
  return (
    <div className={`border-b border-gray-200/60 pb-2 mb-2 xl:pb-3 xl:mb-4 ${className}`}>
      {children}
    </div>
  );
};

interface CardTitleProps {
  children: React.ReactNode;
  className?: string;
}

export const CardTitle: React.FC<CardTitleProps> = ({ children, className = '' }) => {
  return (
    <h3 className={`text-lg font-semibold text-gray-900 ${className}`}>
      {children}
    </h3>
  );
};

interface CardContentProps {
  children: React.ReactNode;
  className?: string;
}

export const CardContent: React.FC<CardContentProps> = ({ children, className = '' }) => {
  return (
    <div className={className}>
      {children}
    </div>
  );
};

interface CardFooterProps {
  children: React.ReactNode;
  className?: string;
}

export const CardFooter: React.FC<CardFooterProps> = ({ children, className = '' }) => {
  return (
    <div className={`border-t border-gray-200/60 pt-3 mt-3 xl:pt-4 xl:mt-4 ${className}`}>
      {children}
    </div>
  );
};

export default Card;
